import { Trans, useTranslation } from "react-i18next";
import SpinnerArea from "../../../../components/SpinnerArea";
import { useContext, useState } from "react";
import { TaskTypeProps } from "./TaskTypeProps";
import MainContext from "../../../../context/mainContext";
import { useNavigate } from "react-router";
import useService from "../../../../hooks/useService";
import tasksService from "../../../../services/tasks.service";
import * as Yup from "yup";
import { PeerReviewApprovalDto } from "../../../../models/task.model";
import { FormikProps, useFormik } from "formik";
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { PeoplePicker } from "../../../../components/SelectPicker/PeoplePickers";
import { IUser } from "../../../../models/user.model";
import styles from "../Task.module.scss";
import { CancelDialog, SaveSuccessDialog } from "../TaskDialogs";
import { RemoveIcon } from "../../../../assets/icons";
import moment from "moment";

const PeerReviewApproval: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service } = useContext(MainContext);
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [cancelDialogShow, cancelDialogShowSet] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [model, loading, ,] = useService(() => tasksService.getApproveReviewers(taskHeader.id));

  const [saving, setSaving] = useState(false);
  const [duplicatedError, setDuplicatedError] = useState<string | undefined>();

  const validationSchema = Yup.object().shape({
    reviewers: Yup.array()
      .min(1, t("requiredField")!)
      .max(3, t("maxAllowedUsers", { max: "3" })!),
  });

  const formik = useFormik<PeerReviewApprovalDto>({
    initialValues: model!,
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const addReviewer = () => {
    if (selectedUser) {
      formik.setFieldValue("reviewers", [...formik.values.reviewers, selectedUser]);
      setSelectedUser(null);
    }
  };

  const removeReviewer = (index: number) => {
    const aux = [...formik.values.reviewers];
    aux.splice(index, 1);

    formik.setFieldValue("reviewers", [...aux]);
  };

  const approveReviewers = (values: PeerReviewApprovalDto) => {
    service(() => tasksService.approveReviewers(values), {
      callback: () => {
        setSaveSuccess(true);
      },
      loader: setSaving,
    });
  };

  const cancel = (formik: FormikProps<PeerReviewApprovalDto>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  return (
    <SpinnerArea loading={loading || saving}>
      <form onSubmit={formik.handleSubmit} className={styles.peerReviewApproval}>
        {taskHeader.workflowType !== "Creation" && (
          <div className={styles.workflowReason}>
            <p>
              <strong>{t("justificationOfTheModification")}</strong>
            </p>
            <p>{taskHeader.workflowReason}</p>
          </div>
        )}
        <p>
          <Trans 
            i18nKey="approvalReviewText"
            components={{
              bold: <strong />,
            }}
            values={{
              dueDate: moment(taskHeader.deadline).format("MM/DD/YYYY"),
            }}
          />
        </p>
        <Grid container spacing={2} item xs={12} md={10} lg={8}>
          <Grid item xs={10} md={6}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("suggestedPeerReviewers")}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formik.values?.reviewers?.map((x, index) => (
                    <TableRow key={`reviewer_${x.email}`}>
                      <TableCell>{x.displayName}</TableCell>
                      <TableCell className={styles.actions}>
                        <Tooltip title={t("delete")} disableInteractive arrow>
                          <IconButton disabled={false} onClick={() => removeReviewer(index)}>
                            <RemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <div className={styles.info}>
              <label className={styles.waring}>{t("approveReviewersInfo")}</label>
            </div>
          </Grid>
          <Grid item xs={10} md={6} className={styles.searchReviewers}>
            <FormControl fullWidth>
              <FormLabel id="selectedProject-label">{t("addPeerReviewers")}</FormLabel>
              <PeoplePicker
                placeholder={t("select")!}
                defaultValue={selectedUser}
                error={duplicatedError}
                onChange={(newValue: IUser | null) => {
                  if (
                    newValue &&
                    formik.values.reviewers.some((x) => x.email.toLowerCase() === newValue?.email.toLowerCase())
                  ) {
                    setDuplicatedError(t("userAlreadyAdded")!);
                  } else {
                    setDuplicatedError(undefined);
                    setSelectedUser(newValue);
                  }
                }}
                id={"addPeerRevieers"}
                readOnly={false}
                onBlur={formik.handleBlur}
              />
            </FormControl>
            <div className={styles.addReviewer}>
              <Button variant="text" disabled={!selectedUser} onClick={addReviewer}>
                {t("add")}
              </Button>
            </div>
          </Grid>
        </Grid>
        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>

          <Button className={styles.save} disabled={!formik.isValid} onClick={() => approveReviewers(formik.values)}>
            {t("approve")}
          </Button>
        </div>
      </form>
      <CancelDialog open={cancelDialogShow} onAccept={() => navigate(-1)} onClose={() => cancelDialogShowSet(false)} />
      <SaveSuccessDialog
        open={saveSuccess}
        onAccept={() => {}}
        onClose={() => setSaveSuccess(false)}
        title={t("saveSuccess")}
      />
    </SpinnerArea>
  );
};

export default PeerReviewApproval;
